import { useLiveQuery } from "dexie-react-hooks";
import { useMemo, useState } from "react";
import { Bell, Flag, Hash, Inbox, Plus, Tag, UserRound, X } from "lucide-react";
import { colorHex, type Collaborator, type Priority, type Project, type Reminder, type Task } from "@todoer/shared";
import { db } from "../lib/db.js";
import { fmtDuration } from "../lib/format.js";
import { sortProjectTree } from "../lib/hooks.js";
import { cmd, dispatch, uuid } from "../lib/sync.js";
import { localTime } from "./DuePicker.js";
import { OptionList, type Option } from "./overlay.js";
import { Avatar, Button, cx, IconButton } from "./ui.js";

export const PRIORITIES: { value: Priority; label: string; short: string; color: string }[] = [
  { value: 4, label: "Priority 1", short: "P1", color: "var(--color-p1)" },
  { value: 3, label: "Priority 2", short: "P2", color: "var(--color-p2)" },
  { value: 2, label: "Priority 3", short: "P3", color: "var(--color-p3)" },
  { value: 1, label: "Priority 4", short: "P4", color: "var(--color-text-faint)" },
];
export const priorityMeta = (p: Priority) => PRIORITIES.find((x) => x.value === p)!;

export function PriorityFlag({ priority, size = 16 }: { priority: Priority; size?: number }) {
  const m = priorityMeta(priority);
  return <Flag size={size} aria-hidden style={{ color: m.color }} fill={priority > 1 ? m.color : "none"} />;
}

export function PriorityList({ value, onSelect }: { value: Priority; onSelect: (p: Priority) => void }) {
  return (
    <OptionList label="Priority" options={PRIORITIES.map((p) => ({
      key: p.short, label: p.label, selected: value === p.value, hint: p.short,
      leading: <PriorityFlag priority={p.value} />, onSelect: () => onSelect(p.value),
    }))} />
  );
}

export const projectLabel = (p: Pick<Project, "isInbox" | "name">) => (p.isInbox ? "Inbox" : p.name);

/** Projects (and optionally their sections) as a searchable list. */
export function ProjectList({ value, sectionValue, onSelect, withSections, exclude, includeNone }: {
  value: string | null; sectionValue?: string | null; onSelect: (projectId: string | null, sectionId: string | null) => void;
  withSections?: boolean; exclude?: (p: Project) => boolean; includeNone?: string;
}) {
  const projects = useLiveQuery(() => db.projects.filter((p) => !p.isArchived).toArray(), [], []);
  const sections = useLiveQuery(() => (withSections ? db.sections.filter((s) => !s.isArchived).toArray() : []), [withSections], []);
  const options = useMemo(() => {
    const out: Option[] = [];
    if (includeNone) out.push({ key: "none", label: includeNone, selected: value === null, onSelect: () => onSelect(null, null) });
    for (const p of sortProjectTree(projects)) {
      if (exclude?.(p)) continue;
      out.push({
        key: p.id, label: projectLabel(p), indent: p.depth, selected: value === p.id && !sectionValue,
        icon: p.isInbox ? Inbox : undefined, iconColor: p.isInbox ? "var(--color-info)" : undefined,
        leading: p.isInbox ? undefined : <Hash size={15} aria-hidden style={{ color: colorHex(p.color) }} />,
        onSelect: () => onSelect(p.id, null),
      });
      if (withSections) {
        for (const s of sections.filter((x) => x.projectId === p.id).sort((a, b) => a.sectionOrder - b.sectionOrder)) {
          out.push({ key: s.id, label: s.name, indent: p.depth + 1.4, selected: sectionValue === s.id, hint: "section",
            leading: <span aria-hidden className="h-3 w-3 shrink-0 rounded-[3px] border border-border-strong" />, onSelect: () => onSelect(p.id, s.id) });
        }
      }
    }
    return out;
  }, [projects, sections, value, sectionValue, withSections, exclude, includeNone, onSelect]);
  return <OptionList label="Project" search={projects.length > 5} placeholder="Find a project" options={options} />;
}

/** Multi-select labels with "create" for new names. */
export function LabelList({ selected, onChange }: { selected: string[]; onChange: (ids: string[]) => void }) {
  const labels = useLiveQuery(() => db.labels.toArray(), [], []);
  const sorted = [...labels].sort((a, b) => a.itemOrder - b.itemOrder || a.name.localeCompare(b.name));
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <OptionList label="Labels" multi search placeholder="Find or create a label" emptyText="No labels yet. Type a name to create one."
      options={sorted.map((l) => ({ key: l.id, label: l.name, selected: selected.includes(l.id), leading: <Tag size={15} aria-hidden style={{ color: colorHex(l.color) }} />, onSelect: () => toggle(l.id) }))}
      onCreate={(text) => {
        const name = text.replace(/^@/, "").replace(/\s+/g, "_");
        if (!name || labels.some((l) => l.name.toLowerCase() === name.toLowerCase())) return null;
        return { label: `Create "@${name}"`, run: () => { const id = uuid(); void dispatch(cmd("label_add", { id, name })).then(() => onChange([...selected, id])); } };
      }} />
  );
}

export const DURATIONS = [15, 30, 45, 60, 90, 120, 180, 240];
export function DurationList({ value, onSelect }: { value: number | null; onSelect: (m: number | null) => void }) {
  return (
    <OptionList label="Duration" options={[
      { key: "none", label: "No duration", selected: value === null, onSelect: () => onSelect(null) },
      ...DURATIONS.map((m) => ({ key: String(m), label: fmtDuration(m), selected: value === m, onSelect: () => onSelect(m) })),
    ]} />
  );
}

export function AssigneeList({ members, value, meId, onSelect }: { members: Collaborator[]; value: string | null; meId?: string; onSelect: (id: string | null) => void }) {
  return (
    <OptionList label="Assignee" search={members.length > 6} options={[
      { key: "none", label: "Unassigned", icon: UserRound, selected: value === null, onSelect: () => onSelect(null) },
      ...members.map((m) => ({ key: m.id, label: m.id === meId ? `${m.name} (me)` : m.name, selected: value === m.id, leading: <Avatar person={m} size={20} />, onSelect: () => onSelect(m.id) })),
    ]} />
  );
}

export const OFFSETS = [0, 5, 10, 15, 30, 60, 120, 1440];
export const offsetLabel = (m: number) => (m === 0 ? "At due time" : m < 60 ? `${m} min before` : m < 1440 ? `${m / 60} h before` : "1 day before");
export const reminderText = (r: Pick<Reminder, "type" | "at" | "minuteOffset">) =>
  r.type === "relative" ? offsetLabel(r.minuteOffset ?? 0) : new Date(r.at!).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export type ReminderDraft = { type: "absolute"; at: string } | { type: "relative"; minuteOffset: number };

/** Choose reminders: relative to the due time, or at a set date and time. */
export function ReminderPanel({ hasDueTime, dueTime, reminders, onAdd, onRemove }: {
  hasDueTime: boolean; dueTime?: string | null; reminders: { key: string; text: string }[]; onAdd: (r: ReminderDraft) => void; onRemove: (key: string) => void;
}) {
  const [at, setAt] = useState("");
  return (
    <div className="text-body">
      {reminders.length > 0 && (
        <ul className="border-b border-border py-1">
          {reminders.map((r) => (
            <li key={r.key} className="flex items-center gap-2 px-3 py-1">
              <Bell size={15} className="text-text-faint" aria-hidden /><span className="flex-1">{r.text}</span>
              <IconButton size="sm" icon={X} label="Remove reminder" onClick={() => onRemove(r.key)} />
            </li>
          ))}
        </ul>
      )}
      <div className="px-3 pb-1 pt-2 text-caption font-semibold uppercase text-text-faint">{hasDueTime ? `Before the due time${dueTime ? ` (${localTime(dueTime)})` : ""}` : "Before the due time"}</div>
      {hasDueTime ? (
        <div className="flex flex-wrap gap-1.5 px-3 pb-2">
          {OFFSETS.map((m) => (
            <button key={m} type="button" onClick={() => onAdd({ type: "relative", minuteOffset: m })}
              className="h-7 rounded-full border border-border px-2.5 text-meta text-text-muted hover:border-border-strong hover:text-text phone:h-10">{offsetLabel(m)}</button>
          ))}
        </div>
      ) : <p className="px-3 pb-2 text-meta text-text-faint">Give the task a time to remind relative to it.</p>}
      <div className="border-t border-border px-3 pb-3 pt-2">
        <div className="pb-1.5 text-caption font-semibold uppercase text-text-faint">At a date and time</div>
        <div className="flex gap-2">
          <input type="datetime-local" aria-label="Reminder date and time" value={at} onChange={(e) => setAt(e.target.value)}
            className="h-8 min-w-0 flex-1 rounded-control border border-border bg-surface-raised px-2 text-body outline-none focus:border-brand phone:h-11" />
          <Button size="sm" variant="primary" icon={Plus} disabled={!at} className="phone:h-11" onClick={() => { onAdd({ type: "absolute", at: new Date(at).toISOString() }); setAt(""); }}>Add</Button>
        </div>
      </div>
    </div>
  );
}

/** Reminders panel bound to a saved task. */
export function TaskReminders({ task }: { task: Task }) {
  const reminders = useLiveQuery(() => db.reminders.where("taskId").equals(task.id).toArray(), [task.id], []);
  return (
    <ReminderPanel hasDueTime={!!task.due?.datetime} dueTime={task.due?.datetime}
      reminders={reminders.map((r) => ({ key: r.id, text: reminderText(r) }))}
      onAdd={(r) => void dispatch(cmd("reminder_add", { id: uuid(), taskId: task.id, ...r }))}
      onRemove={(id) => void dispatch(cmd("reminder_delete", { id }))} />
  );
}

export function ColorDotIcon({ color, className }: { color: string; className?: string }) {
  return <span aria-hidden className={cx("h-2.5 w-2.5 shrink-0 rounded-full", className)} style={{ background: colorHex(color) }} />;
}
